import React from 'react';
import {
  IconCheck,
  IconX,
  IconInfoCircle,
  IconLoader2,
  IconAlertCircle,
} from '@tabler/icons-react';
import { StatusMessage } from '../types';
import styles from './components.module.scss';
import { useMantineTheme, useMantineColorScheme, Tooltip } from '@mantine/core';
import { useAppSelector } from '@store/hooks';
import { useTranslation } from 'react-i18next';

const statusConfig: Record<
  StatusMessage['type'],
  {
    color: string;
    icon: React.ReactNode;
  }
> = {
  success: {
    color: 'teal',
    icon: <IconCheck size={16} className={styles.statusSection__icon} />,
  },
  error: {
    color: 'red',
    icon: <IconX size={16} className={styles.statusSection__icon} />,
  },
  info: {
    color: 'blue',
    icon: <IconInfoCircle size={16} className={styles.statusSection__icon} />,
  },
  loading: {
    color: 'yellow',
    icon: <IconLoader2 size={16} className={`${styles.statusSection__icon} ${styles.rotating}`} />,
  },
  warning: {
    color: 'orange',
    icon: <IconAlertCircle size={16} className={styles.statusSection__icon} />,
  },
};

interface StatusSectionProps {
  status?: StatusMessage;
}

export const StatusSection: React.FC<StatusSectionProps> = ({ status: propStatus }) => {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const { t } = useTranslation();

  // Obtener el estado global de la aplicación si no se proporciona uno
  const globalStatus = useAppSelector(state => state.app.status);

  // Asegurarse de que siempre tengamos un estado válido con un mensaje
  const status = propStatus ||
    globalStatus || {
      type: 'info',
      message: t('status.ready'),
      details: t('status.details'),
    };

  // Asegurarse de que el mensaje siempre tenga un valor
  const message = status.message || t('status.ready');

  const config = statusConfig[status.type];

  // Obtener el color de fondo basado en el tipo de estado
  const bgColor = theme.colors[config.color][colorScheme === 'dark' ? 7 : 1];
  const textColor = theme.colors[config.color][colorScheme === 'dark' ? 0 : 9];

  // Determinar si se debe mostrar el tooltip
  const shouldShowTooltip = status.type === 'error' && status.details;
  const isMessageLong = message.length > 30;

  // Contenido del estado
  const statusContent = (
    <div
      className={styles.statusSection}
      style={{
        backgroundColor: bgColor,
        color: textColor,
      }}
    >
      {config.icon}
      <span className={styles.statusSection__text}>{message}</span>
    </div>
  );

  // Renderizar con o sin tooltip según corresponda
  return shouldShowTooltip || isMessageLong ? (
    <Tooltip
      label={shouldShowTooltip ? status.details : message}
      position="top"
      withArrow
      multiline
      w={300}
      transitionProps={{ transition: 'fade', duration: 200 }}
    >
      {statusContent}
    </Tooltip>
  ) : (
    statusContent
  );
};
